import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useNotifications } from "../../contexts/NotificationContext";
import { useOffline } from "../../contexts/OfflineContext";
import { Button } from "../ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { 
    Bell, Sun, Moon, LogOut, User, Settings, Menu, X,
    Building2, FolderKanban, Users, FileText, Receipt,
    CalendarDays, UserCog, UsersRound, Clock, Sparkles, FolderOpen
} from "lucide-react";
import { getInitials } from "../../lib/utils";
import { cn } from "../../lib/utils";
import { SyncStatusIndicator } from "../mobile/SyncStatusIndicator";

// Mobile menu items
const mobileMenuItems = [
    { path: "/dashboard", label: "Dashboard", icon: Building2 },
    { path: "/clients", label: "Clients", icon: Users },
    { path: "/projects", label: "Projets", icon: FolderKanban },
    { path: "/quotes", label: "Devis", icon: FileText },
    { path: "/invoices", label: "Factures", icon: Receipt },
    { path: "/documents", label: "Documents", icon: FolderOpen },
    { path: "/calendar", label: "Calendrier", icon: CalendarDays },
    { path: "/team", label: "Equipe", icon: UserCog },
    { path: "/users", label: "Utilisateurs", icon: UsersRound },
    { path: "/time-tracking", label: "Suivi temps", icon: Clock },
    { path: "/ai-assistant", label: "Assistant IA", icon: Sparkles },
];

export const Header = () => {
    const { user, logout } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const { unreadCount } = useNotifications() || {};
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    const handleMobileNavigate = (path) => {
        navigate(path);
        setMobileMenuOpen(false);
    };

    return (
        <>
            <header 
                className="sticky top-0 z-30 h-14 md:h-16 bg-card/95 backdrop-blur-xl border-b border-border mobile-header safe-area-top" 
                data-testid="header"
            >
                <div className="flex h-full items-center justify-between px-4 md:px-6">
                    {/* Mobile: Logo + Menu */}
                    <div className="flex items-center gap-3 md:hidden">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="h-9 w-9"
                            data-testid="mobile-menu-toggle"
                        >
                            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        </Button>
                        <div className="flex items-center gap-2">
                            <img src="/orkestria-logo.png" alt="Orkestria" className="w-8 h-8 rounded-lg object-contain" />
                            <span className="font-heading font-bold text-base">Orkestria</span>
                        </div>
                    </div>

                    {/* Desktop: Empty space */}
                    <div className="hidden md:flex flex-1 max-w-md" />

                    {/* Right actions */}
                    <div className="flex items-center gap-1 md:gap-2">
                        {/* Sync Status Indicator */}
                        <SyncStatusIndicator />

                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={toggleTheme}
                            data-testid="theme-toggle"
                            className="h-9 w-9 text-muted-foreground hover:text-foreground"
                        >
                            {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                        </Button>

                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate("/notifications")}
                            data-testid="notifications-btn"
                            className="relative h-9 w-9 text-muted-foreground hover:text-foreground"
                        >
                            <Bell className="w-5 h-5" />
                            {unreadCount > 0 && (
                                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1" data-testid="unread-badge">
                                    {unreadCount > 99 ? "99+" : unreadCount}
                                </span>
                            )}
                            {!unreadCount && <span className="absolute top-1 right-1 w-2 h-2 bg-accent rounded-full" />}
                        </Button>

                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="relative h-9 w-9 rounded-full" data-testid="user-menu-trigger">
                                    <Avatar className="h-9 w-9">
                                        <AvatarImage src={user?.picture} alt={user?.name} />
                                        <AvatarFallback className="bg-primary text-primary-foreground text-sm font-medium">
                                            {getInitials(user?.name)}
                                        </AvatarFallback>
                                    </Avatar>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56" data-testid="user-menu">
                                <div className="px-2 py-1.5">
                                    <p className="text-sm font-medium">{user?.name}</p>
                                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                                </div>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-profile">
                                    <User className="w-4 h-4 mr-2" />
                                    Profil
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                                    <Settings className="w-4 h-4 mr-2" />
                                    Parametres
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="menu-logout">
                                    <LogOut className="w-4 h-4 mr-2" />
                                    Deconnexion
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
            </header>

            {/* Mobile Slide-out Menu */}
            <div 
                className={cn(
                    "fixed inset-0 z-40 md:hidden transition-all duration-300",
                    mobileMenuOpen ? "visible" : "invisible"
                )}
            >
                {/* Backdrop */}
                <div 
                    className={cn(
                        "absolute inset-0 bg-black/50 transition-opacity",
                        mobileMenuOpen ? "opacity-100" : "opacity-0"
                    )}
                    onClick={() => setMobileMenuOpen(false)}
                />
                
                {/* Menu Panel */}
                <div 
                    className={cn(
                        "absolute top-0 left-0 bottom-0 w-72 bg-card shadow-xl transition-transform duration-300 safe-area-top",
                        mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
                    )}
                >
                    {/* Header */}
                    <div className="flex items-center gap-3 p-4 border-b border-border">
                        <img src="/orkestria-logo.png" alt="Orkestria" className="w-10 h-10 rounded-lg object-contain" />
                        <div>
                            <h2 className="font-heading font-bold">Orkestria</h2>
                            <p className="text-xs text-muted-foreground">Gestion de Projets</p>
                        </div>
                    </div>

                    {/* Navigation */}
                    <nav className="p-2 overflow-y-auto max-h-[calc(100vh-10rem)]">
                        {mobileMenuItems.map((item) => {
                            const Icon = item.icon;
                            return (
                                <button
                                    key={item.path}
                                    onClick={() => handleMobileNavigate(item.path)}
                                    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left hover:bg-muted transition-colors touch-manipulation"
                                    data-testid={`mobile-menu-${item.path.replace("/", "")}`}
                                >
                                    <Icon className="w-5 h-5 text-muted-foreground" />
                                    <span className="font-medium">{item.label}</span>
                                </button>
                            );
                        })}
                    </nav>

                    {/* User info */}
                    <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border bg-card safe-area-bottom">
                        <div className="flex items-center gap-3">
                            <Avatar className="h-10 w-10">
                                <AvatarImage src={user?.picture} alt={user?.name} />
                                <AvatarFallback className="bg-primary text-primary-foreground">
                                    {getInitials(user?.name)}
                                </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">{user?.name}</p>
                                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
